import { action, observable, makeObservable, computed } from 'mobx';
import type { EventPanelMetadata } from '@mark43/cad-api';
import { O } from 'ts-toolbelt';
import { alphanumericStringComparator } from '../../common/sorting/comparators';

const defaultOptionalMetadataValues: {
    [Key in O.OptionalKeys<EventPanelMetadata>]: undefined;
} = {
    agencyEventNumber: undefined,
    callForServiceDescription: undefined,
    eventId: undefined,
    eventLocationId: undefined,
    eventLocationLatitude: undefined,
    eventLocationLongitude: undefined,
    eventPhoneCall: undefined,
    highestPriorityLocationCaution: undefined,
    scheduledEventActivationBufferInMinutes: undefined,
    scheduledEventFrequencyType: undefined,
    scheduledEventScheduledDateUtc: undefined,
    scheduledEventScheduledEndDateUtc: undefined,
    timeTransmitted: undefined,
    parentAgencyEventId: undefined,
    primaryReportingParty: undefined,
    staffCount: undefined,
};

export class EventPanelMetadataStore {
    @observable
    public readonly state: { metadata?: EventPanelMetadata } = {
        metadata: undefined,
    };

    constructor(initialMetadata?: EventPanelMetadata) {
        if (initialMetadata) {
            this.setEventPanelMetadata(initialMetadata);
        }
        makeObservable(this);
    }

    @action
    setEventPanelMetadata(eventPanelMetadata: EventPanelMetadata): void {
        if (this.state.metadata) {
            // Because `eventPanelMetadata` can be sparse, merging `eventPanelMetadata`
            // into `this.state.metadata` won't override keys that are present in `this.state.metatdata`
            // but not in `eventPanelMetadata`
            //
            // To account for these keys, we determine which keys are optional, and merge these
            // into `this.state.metadata` before merging the `eventPanelMetadata`
            Object.assign(this.state.metadata, defaultOptionalMetadataValues, eventPanelMetadata);
        } else {
            this.state.metadata = eventPanelMetadata;
        }
    }

    @computed
    get hasCrossClassEvents(): boolean {
        const filtered = this.state.metadata?.linkedAndCrossClassEvents.filter(
            (event) => !event.isLinkedEvent
        );
        return !!filtered && filtered.length > 0;
    }

    @computed
    get eventRens(): string[] {
        return Object.values(this.state.metadata?.agencyIdToReportingEventNumbers ?? {})
            .flat()
            .sort(alphanumericStringComparator);
    }

}
